import { useCallback, useState } from 'react'

import dayjs from 'dayjs'
import { useNavigate } from 'react-router'

import { Post } from '@tribeplatform/gql-client/types'
import { useUpdatePost } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { DateValue } from '@tribeplatform/react-ui-kit/DateTimePicker'
import { Text } from '@tribeplatform/react-ui-kit/Text'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { HIGHLIGHTED_POST_QUERY_PARAM } from '../../common/constants/post.js'
import { T, useI18n } from '../../i18n/index.js'

import { SchedulePostPopup } from './SchedulePostPopup.js'

interface Props {
  postId?: Post['id']
  scheduledFor?: DateValue
}

export const ScheduledPostInfo = ({ postId, scheduledFor }: Props) => {
  const [schedulePopupOpen, setSchedulePopupOpen] = useState(false)
  const { mutateAsync: updatePost } = useUpdatePost()
  const navigate = useNavigate()
  const { $t } = useI18n()

  const onReschedule = useCallback(
    async (date: DateValue) => {
      if (!postId) {
        return
      }

      try {
        await updatePost({
          id: postId,
          input: { scheduledFor: date.toLocaleString(), publish: false },
        })
        navigate(
          `/settings/moderation/all/scheduled-posts?${HIGHLIGHTED_POST_QUERY_PARAM}=${postId}`,
        )
      } catch (error) {
        toast({
          title: $t({
            id: 'Generics.Error',
            defaultMessage: 'Error',
          }),
          description: $t({
            id: 'Generics.SomethingWentWrong',
            defaultMessage: 'Something went wrong',
          }),
        })
      }
    },
    [postId, updatePost, navigate, $t],
  )

  if (!scheduledFor) {
    return null
  }

  return (
    <>
      <div className="flex gap-2 items-center flex-wrap grow text-sm italic">
        <Text className="flex items-center gap-2 text-content-subdued text-sm">
          <T
            id="Moderation.ScheduledPosts.ScheduledPostAlert"
            defaultMessage="Scheduled for {date}."
            values={{
              date: dayjs(scheduledFor).format('DD/MMM/YYYY HH:mm'),
            }}
          />
        </Text>
        {!!onReschedule && (
          <Button
            size="sm"
            className="shrink-0 !p-0 text-sm italic"
            variant="plain"
            onClick={() => setSchedulePopupOpen(true)}
          >
            <T id="Generics.Reschedule" defaultMessage="Reschedule" />
          </Button>
        )}
      </div>
      {schedulePopupOpen && (
        <SchedulePostPopup
          open={schedulePopupOpen}
          value={scheduledFor}
          setOpen={setSchedulePopupOpen}
          onSubmit={value => {
            onReschedule?.(value)
            setSchedulePopupOpen(false)
          }}
        />
      )}
    </>
  )
}
